import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, PencilLine, ShieldAlert } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import {
  AronApiBaseContext,
  useAronConversationInfo,
  useAronMessages,
  useAronPendingWriteThreads,
} from "../api/aronApi";
import {
  useAronWorkbenchStore,
  type AronApprovalRequest,
} from "../store/aronWorkbenchStore";
import { pendingWrites } from "../utils/attention";
import { AronWriteProposal } from "./AronWriteProposal";

/**
 * The write gate as a MODAL — ARON never writes to the database on its own, and
 * when it asks, the ask stops being something you can scroll past. Same cards,
 * same endpoints as the inline proposal in the transcript: dismissing the modal
 * decides nothing, the proposal simply stays in the conversation.
 */
function AronApprovalModal({ approval }: { approval: AronApprovalRequest }) {
  const navigate = useNavigate();
  const close = useAronWorkbenchStore((s) => s.closeApproval);
  const openConversation = useAronWorkbenchStore((s) => s.openConversation);
  const { data: messages = [] } = useAronMessages(approval.convId);
  const conv = useAronConversationInfo(approval.convId);
  // Older threads are past page 1 of the list; the pending-writes set is not paginated.
  const { data: pendingThreads = [] } = useAronPendingWriteThreads();

  const writes = pendingWrites(messages);
  const title =
    conv?.title ||
    pendingThreads.find((t) => t.id === approval.convId)?.title ||
    "Rozmowa";

  // Every proposal decided (here or in the transcript) → nothing left to gate.
  useEffect(() => {
    if (writes.length === 0) close();
  }, [writes.length, close]);

  if (writes.length === 0) return null;

  return (
    <Modal
      open
      onClose={close}
      size="lg"
      title={
        writes.length === 1
          ? "ARON prosi o zgodę na zapis"
          : `ARON prosi o zgodę na ${writes.length} zapisy`
      }
    >
      <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-200">
        <ShieldAlert size={14} className="mt-px shrink-0" />
        <span>
          Nic nie trafiło jeszcze do bazy. Zapis wykona się dopiero po Twoim
          zatwierdzeniu i z Twoimi uprawnieniami. Zamknięcie okna niczego nie
          rozstrzyga — propozycja zostaje w rozmowie.
        </span>
      </div>

      <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
        <PencilLine size={12} /> Rozmowa: <span className="font-medium">{title}</span>
      </p>

      <div className="mt-2 space-y-2">
        {writes.map((w) => (
          <AronWriteProposal
            key={w.id}
            conversationId={approval.convId}
            write={w}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
        <button
          type="button"
          onClick={() => {
            openConversation(approval.convId);
            close();
            navigate(`/aron?c=${approval.convId}`);
          }}
          className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:underline"
        >
          Otwórz rozmowę i zobacz kontekst <ArrowRight size={12} />
        </button>
        <button
          type="button"
          onClick={close}
          className="border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300"
        >
          Później
        </button>
      </div>
    </Modal>
  );
}

/**
 * Mounted once, globally (ProtectedLayout): the gate must be able to pop
 * wherever the conversation is live — the ARON workbench or the side dock next
 * to a completely different page.
 */
export function AronApprovalModalHost() {
  const approval = useAronWorkbenchStore((s) => s.approval);
  if (!approval) return null;
  return (
    // The proposal was made by a persona; execute/reject must go back to ITS API
    // base, not the "/aron" default this host would otherwise inherit.
    <AronApiBaseContext.Provider value={approval.apiBase}>
      <AronApprovalModal approval={approval} />
    </AronApiBaseContext.Provider>
  );
}
